import React, { useState, useEffect, useCallback, useRef } from 'react';
import './UGStudentDashboard.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaBell, FaTimes, FaSignOutAlt, FaClipboardList,
  FaCheckCircle, FaExclamationCircle, FaChevronRight, FaChevronLeft,
  FaBookMedical, FaPlus, FaSearch, FaHandHoldingMedical,
  FaPenFancy, FaCertificate, FaTrash, FaPencilAlt,
  FaExchangeAlt, FaStethoscope, FaHouseUser, FaShieldVirus, FaUniversity, FaBookReader,
  FaChalkboardTeacher, FaClipboardCheck, FaUserTie, FaUserMd, FaUser, FaDownload
} from 'react-icons/fa';
import PasswordChangeModal from '../StudentDashboard/PasswordChangeModal';
import ManageAccountModal from '../StudentDashboard/ManageAccountModal';
import UGElectives from './UGElectives';
import UGEce from './UGEce';
import UGFap from './UGFap';
import UGPandemicModule from './UGPandemicModule';
import UGFoundationCourse from './UGFoundationCourse';
import UGSdl from './UGSdl';
import UGConference from './UGConference';
import UGDoap from './UGDoap';
import UGProfessionalism from './UGProfessionalism';
import UGInternship from './UGInternship';
import UGCertificates from './UGCertificates';
import { generateUGLogbookPDF } from './ugPdfGenerator';
import Pagination, { ITEMS_PER_PAGE } from './Pagination';

const SETTING_OPTIONS = ['OPD', 'IPD', 'Emergency', 'Skill Lab/Simulation', 'Community/Field Visit', 'OT'];
const PHASE_ORDER = ['Phase 1', 'Phase 2', 'Phase 3 Part 1', 'Phase 3 Part 2'];
// NMC CBME phase durations, shown next to each phase heading so students can
// place themselves in the course timeline at a glance.
const PHASE_YEAR_LABEL = {
  'Phase 1': '1 Year',
  'Phase 2': '1.5 Years',
  'Phase 3 Part 1': '1 Year',
  'Phase 3 Part 2': '1 Year'
};

const SUBMISSION_STATUS_LABELS = {
  pending: 'Pending review',
  approved: 'Certified',
  needs_revision: 'Needs revision',
  rejected: 'Rejected'
};

const REFLECTION_TYPES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'case', label: 'Case Reflection' }
];

const REFLECTION_TYPE_LABEL = { weekly: 'Weekly', monthly: 'Monthly', case: 'Case Reflection' };

const REFLECTION_PERIOD_FIELD = {
  weekly: { label: 'Week / Period', placeholder: 'e.g. Week 1 – July 2026' },
  monthly: { label: 'Month / Period', placeholder: 'e.g. July 2026' },
  case: { label: 'Case', placeholder: 'e.g. 45M with acute MI – ward rounds' }
};

const REFLECTION_TABS = [
  { value: '', label: 'All' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'case', label: 'Case Reflection' }
];

const emptyReflectionForm = () => ({
  type: 'weekly',
  period: '',
  date: new Date().toISOString().slice(0, 10),
  reflectionText: '',
  learningOutcome: ''
});

function levelOptionsFor(nmcLevel) {
  if (nmcLevel === 'P') {
    return ['Observed', 'Performed under supervision', 'Performed independently'];
  }
  // SH-level competencies (the only other loggable level) don't expect
  // independent performance yet per NMC's own definitions.
  return ['Observed', 'Performed under supervision'];
}


const UGStudentDashboard = ({ onNavigate }) => {
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showManageAccount, setShowManageAccount] = useState(false);
  const profileMenuRef = useRef(null);

  const [subjects, setSubjects] = useState([]);
  const [myLogs, setMyLogs] = useState([]);
  const [dueNow, setDueNow] = useState({ currentPhase: null, due: [], overdue: [] });
  const [loading, setLoading] = useState(true);

  // 'dashboard' | 'elogbook-main' | 'select-subject' | 'subject' | 'submissions' | 'aetcom-main'
  const [view, setView] = useState('dashboard');
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [subjectSearchTerm, setSubjectSearchTerm] = useState('');
  const [submissionStatusFilter, setSubmissionStatusFilter] = useState('');
  const [submissionSubjectFilter, setSubmissionSubjectFilter] = useState('');
  const [submissionDateFrom, setSubmissionDateFrom] = useState('');
  const [submissionDateTo, setSubmissionDateTo] = useState('');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [submissionPage, setSubmissionPage] = useState(1);

  const [logModalCompetency, setLogModalCompetency] = useState(null);
  const [logForm, setLogForm] = useState({ dateOfEncounter: '', levelAchieved: '', settingType: '', caseContext: '', note: '' });
  const [submitting, setSubmitting] = useState(false);

  const [aetcomModules, setAetcomModules] = useState([]);
  const [aetcomLoading, setAetcomLoading] = useState(false);
  const [aetcomModalModule, setAetcomModalModule] = useState(null);
  const [aetcomForm, setAetcomForm] = useState({ status: 'in_progress', activitiesCompleted: '', reflectionNotes: '' });
  const [aetcomSubmitting, setAetcomSubmitting] = useState(false);

  const [reflections, setReflections] = useState([]);
  const [reflectionsLoading, setReflectionsLoading] = useState(false);
  const [reflectionTab, setReflectionTab] = useState('');
  const [reflectionPage, setReflectionPage] = useState(1);
  const [reflectionModalEntry, setReflectionModalEntry] = useState(null); // null = closed, 'new' = create, object = edit
  const [reflectionForm, setReflectionForm] = useState(emptyReflectionForm());
  const [reflectionSubmitting, setReflectionSubmitting] = useState(false);


  const token = localStorage.getItem('token');

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (onNavigate) {
      onNavigate('login');
    } else {
      window.location.reload();
    }
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/notices/dashboard/students`, {
        headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setNotifications(data.notices || []);
        const unread = (data.notices || []).filter(n =>
          !(n.readBy || []).some(r => r.userId === (user?.id || user?._id))
        ).length;
        setUnreadCount(unread);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  }, [authHeaders, user]);

  const markNotificationRead = async (id) => {
    try {
      await fetch(`${collegeConfig.api.baseURL}/notices/${id}/read`, {
        method: 'PUT',
        headers: authHeaders()
      });
      fetchNotifications();
    } catch (err) {
      console.error('Error marking notice read:', err);
    }
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [subjectsRes, logsRes, dueRes] = await Promise.all([
        fetch(`${collegeConfig.api.baseURL}/ug/subjects`, { headers: authHeaders() }),
        fetch(`${collegeConfig.api.baseURL}/ug/competency-logs/mine`, { headers: authHeaders() }),
        fetch(`${collegeConfig.api.baseURL}/ug/competency-logs/due-now`, { headers: authHeaders() })
      ]);
      const subjectsData = await subjectsRes.json();
      const logsData = await logsRes.json();
      const dueData = await dueRes.json();

      if (subjectsData.success) setSubjects(subjectsData.subjects || []);
      if (logsData.success) setMyLogs(logsData.logs || []);
      if (dueData.success) setDueNow({ currentPhase: dueData.currentPhase, due: dueData.due || [], overdue: dueData.overdue || [] });
    } catch (err) {
      console.error('Error fetching UG dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      if (parsedUser.requirePasswordChange) {
        setShowPasswordChange(true);
      }
    }
  }, []);

  const handlePasswordChanged = () => {
    setShowPasswordChange(false);
    const storedUser = localStorage.getItem('user');
    if (storedUser) setUser(JSON.parse(storedUser));
  };

  // Close profile menu when clicking outside
  useEffect(() => {
    if (!showProfileMenu) return;
    const handleOutsideClick = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [showProfileMenu]);

  useEffect(() => {
    if (!user || showPasswordChange) return;
    fetchDashboardData();
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, showPasswordChange]);

  // Push a history entry per view so the browser back button steps through
  // E-Logbook sections instead of leaving the app. Landing back on the
  // dashboard (either via an explicit "Back to Dashboard" or by popping past
  // our tracked history) always re-fetches so the data shown is current.
  const navigateToView = (newView, extra = {}) => {
    setView(newView);
    if (newView !== 'subject') setSelectedSubject(null);
    if (newView === 'dashboard') {
      fetchDashboardData();
      fetchNotifications();
    }
    window.history.pushState({ page: 'ug-dashboard', view: newView, ...extra }, '', window.location.href);
    // Every view is a fresh "page" to the student — land at its top rather
    // than wherever the previous view happened to be scrolled to.
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    setSubmissionPage(1);
  }, [submissionStatusFilter, submissionSubjectFilter, submissionDateFrom, submissionDateTo]);

  useEffect(() => {
    setReflectionPage(1);
  }, [reflectionTab]);

  useEffect(() => {
    const handlePopState = (event) => {
      const state = event.state;
      if (state && state.page === 'ug-dashboard') {
        if (state.view === 'subject' && state.subjectId) {
          fetchSubjectById(state.subjectId);
          setView('subject');
        } else {
          setSelectedSubject(null);
          setView(state.view);
          if (state.view === 'dashboard') {
            fetchDashboardData();
            fetchNotifications();
          }
          if (state.view === 'aetcom-main') {
            fetchAetcomModules();
          }
          if (state.view === 'reflections-main') {
            fetchReflections();
          }
        }
        window.scrollTo(0, 0);
      } else {
        // Popped past everything we track — reload for a clean refresh of the dashboard.
        window.location.reload();
      }
    };

    window.addEventListener('popstate', handlePopState);

    if (!window.history.state || window.history.state.page !== 'ug-dashboard') {
      window.history.pushState({ page: 'ug-dashboard', view: 'dashboard' }, '', window.location.href);
    }

    return () => window.removeEventListener('popstate', handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approvedCodesFor = (subjectId) =>
    new Set(myLogs.filter(l => (l.subjectId === subjectId || l.subjectId?._id === subjectId) && l.status === 'approved').map(l => l.competencyCode));

  const subjectProgress = (subject) => {
    if (!subject.loggableCompetencies) return { done: 0, total: 0 };
    const approved = approvedCodesFor(subject._id);
    return { done: approved.size, total: subject.loggableCompetencies };
  };

  const fetchSubjectById = async (subjectId) => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/subjects/${subjectId}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setSelectedSubject(data.subject);
        return true;
      }
    } catch (err) {
      console.error('Error opening subject:', err);
    }
    return false;
  };

  const openSubject = async (subjectId) => {
    const ok = await fetchSubjectById(subjectId);
    if (ok) navigateToView('subject', { subjectId });
  };

  const fetchAetcomModules = async () => {
    try {
      setAetcomLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/aetcom`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setAetcomModules(data.modules || []);
    } catch (err) {
      console.error('Error fetching AETCOM modules:', err);
    } finally {
      setAetcomLoading(false);
    }
  };

  const openAetcom = () => {
    navigateToView('aetcom-main');
    fetchAetcomModules();
  };

  const openAetcomModal = (module) => {
    setAetcomModalModule(module);
    setAetcomForm({
      // The stored/badge status stays "Not Started" until the student actually
      // saves something — but the dropdown itself defaults to "In Progress" as
      // the suggested next step, rather than re-selecting "Not Started".
      status: module.status && module.status !== 'not_started' ? module.status : 'in_progress',
      activitiesCompleted: module.activitiesCompleted || '',
      reflectionNotes: module.reflectionNotes || ''
    });
  };

  const submitAetcomProgress = async () => {
    try {
      setAetcomSubmitting(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/aetcom/${aetcomModalModule.number}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(aetcomForm)
      });
      const data = await res.json();
      if (data.success) {
        setAetcomModalModule(null);
        await fetchAetcomModules();
      } else {
        alert(data.message || 'Failed to save progress');
      }
    } catch (err) {
      console.error('Error saving AETCOM progress:', err);
    } finally {
      setAetcomSubmitting(false);
    }
  };

  // Explicit resubmit after a "needs revision" decision — editing the
  // module's progress alone does not flip its review status back to pending.
  const resubmitAetcomModule = async (moduleNumber) => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/aetcom/${moduleNumber}/resubmit`, {
        method: 'PUT',
        headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) {
        await fetchAetcomModules();
      } else {
        alert(data.message || 'Failed to resubmit module');
      }
    } catch (err) {
      console.error('Error resubmitting AETCOM module:', err);
    }
  };

  const fetchReflections = async () => {
    try {
      setReflectionsLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/reflections/mine`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setReflections(data.reflections || []);
    } catch (err) {
      console.error('Error fetching reflections:', err);
    } finally {
      setReflectionsLoading(false);
    }
  };

  const openReflections = () => {
    navigateToView('reflections-main');
    fetchReflections();
  };

  const openNewReflectionModal = () => {
    setReflectionForm(emptyReflectionForm());
    setReflectionModalEntry('new');
  };

  const openEditReflectionModal = (entry) => {
    setReflectionForm({
      type: entry.type,
      period: entry.period,
      date: new Date(entry.date).toISOString().slice(0, 10),
      reflectionText: entry.reflectionText,
      learningOutcome: entry.learningOutcome || ''
    });
    setReflectionModalEntry(entry);
  };

  const submitReflection = async () => {
    if (!reflectionForm.period.trim() || !reflectionForm.reflectionText.trim()) {
      alert('Please fill in Period / Case and Reflection before saving.');
      return;
    }
    const isEdit = reflectionModalEntry && reflectionModalEntry !== 'new';
    try {
      setReflectionSubmitting(true);
      const res = await fetch(
        `${collegeConfig.api.baseURL}/ug/reflections${isEdit ? `/${reflectionModalEntry._id}` : ''}`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: authHeaders(),
          body: JSON.stringify(reflectionForm)
        }
      );
      const data = await res.json();
      if (data.success) {
        setReflectionModalEntry(null);
        await fetchReflections();
      } else {
        alert(data.message || 'Failed to save reflection');
      }
    } catch (err) {
      console.error('Error saving reflection:', err);
    } finally {
      setReflectionSubmitting(false);
    }
  };

  const deleteReflection = async (entry) => {
    if (!window.confirm('Delete this reflection entry? This cannot be undone.')) return;
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/reflections/${entry._id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) {
        await fetchReflections();
      } else {
        alert(data.message || 'Failed to delete reflection');
      }
    } catch (err) {
      console.error('Error deleting reflection:', err);
    }
  };

  // Explicit resubmit after a "needs revision" decision — editing the
  // reflection alone does not flip its review status back to pending.
  const resubmitReflection = async (entry) => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/reflections/${entry._id}/resubmit`, {
        method: 'PUT',
        headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) {
        await fetchReflections();
      } else {
        alert(data.message || 'Failed to resubmit reflection');
      }
    } catch (err) {
      console.error('Error resubmitting reflection:', err);
    }
  };

  const openLogModal = (competency) => {
    setLogModalCompetency(competency);
    setLogForm({
      dateOfEncounter: new Date().toISOString().slice(0, 10),
      levelAchieved: '',
      settingType: '',
      caseContext: '',
      note: ''
    });
  };

  const submitLog = async () => {
    if (!logForm.levelAchieved || !logForm.settingType) {
      alert('Please select Level Achieved and Setting before submitting.');
      return;
    }
    try {
      setSubmitting(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/competency-logs`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          subjectId: selectedSubject._id,
          competencyCode: logModalCompetency.code,
          ...logForm
        })
      });
      const data = await res.json();
      if (data.success) {
        setLogModalCompetency(null);
        await fetchDashboardData();
      } else {
        alert(data.message || 'Failed to submit entry');
      }
    } catch (err) {
      console.error('Error submitting UG competency log:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Representative log per competency code — an approved log always wins
  // (certified permanently), otherwise the most recent attempt (myLogs is
  // already sorted newest-first by the /mine route). Returns the full log
  // object (not just the status string) so callers can also reach
  // log.review?.feedback for needs_revision/rejected entries.
  const codeLogMap = () => {
    const map = {};
    myLogs.forEach(l => {
      if (!map[l.competencyCode] || l.status === 'approved') {
        map[l.competencyCode] = l;
      }
    });
    return map;
  };

  const subjectNameFor = (subjectId) => {
    const subject = subjects.find(s => s._id === subjectId || s._id === subjectId?._id);
    return subject ? `${subject.name} (${subject.code})` : 'Subject';
  };

  // Fetches every UG module's data fresh (not just what's currently loaded
  // in this component's state, since most modules live in their own child
  // components) and hands it to ugPdfGenerator.js — same branded layout as
  // the PG e-logbook PDF (frontend/src/components/StudentDashboard/pdfGenerator.js).
  const handleDownloadPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const studentName = `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() || 'Student';

      const [
        subjectsRes, logsRes, aetcomRes, pandemicRes, foundationRes,
        reflectionsRes, electivesRes, eceRes, familiesRes, visitsRes,
        conferenceRes, doapRes, professionalismRes, postingsRes, activitiesRes,
        batchesRes, additionalInfoRes
      ] = await Promise.allSettled([
        fetch(`${collegeConfig.api.baseURL}/ug/subjects`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/ug/competency-logs/mine`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/ug/aetcom`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/ug/pandemic`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/ug/foundation-course`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/ug/reflections/mine`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/ug/electives/mine`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/ug/ece/mine`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/ug/fap/families/mine`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/ug/fap/visits/mine`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/ug/conference/mine`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/ug/doap/mine`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/ug/professionalism/mine`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/ug/internship/postings/mine`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/ug/internship/activities/mine`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/ug/batches`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/additional-info/student`, { headers: authHeaders() }).then(r => r.json())
      ]);

      const ok = (res, key) => (res.status === 'fulfilled' && res.value.success ? res.value[key] || [] : []);

      const batchId = user.profile?.ugBatchId?._id || user.profile?.ugBatchId;
      const batches = ok(batchesRes, 'batches');
      const rawBatchLabel = batches.find(b => b._id === batchId)?.batchLabel || '';
      // Admins often type the full label as "MBBS 2025-26" (see ManageUGBatches.js
      // placeholder); strip a leading "MBBS" so it doesn't repeat next to the
      // "Course" field, which already states the program name.
      const batchLabel = rawBatchLabel.replace(/^\s*MBBS\s*[-–—]?\s*/i, '').trim() || rawBatchLabel;
      const courseName = 'MBBS';

      const additionalInfo = {};
      if (additionalInfoRes.status === 'fulfilled' && additionalInfoRes.value.success) {
        (additionalInfoRes.value.fields || []).forEach(f => {
          if (f.key && f.submission?.value) additionalInfo[f.key] = f.submission.value;
        });
      }

      const studentInfo = {
        email: user.email,
        profile: { registrationNumber: user.profile?.registrationNumber, batch: batchLabel },
        additionalInfo
      };

      await generateUGLogbookPDF(studentName, courseName, {
        collegeName: collegeConfig.collegeName,
        shortName: collegeConfig.shortName,
        logo: collegeConfig.logo,
        copyrightYear: collegeConfig.copyrightYear,
        appName: collegeConfig.appName
      }, studentInfo, {
        subjects: ok(subjectsRes, 'subjects'),
        competencyLogs: ok(logsRes, 'logs'),
        aetcomModules: ok(aetcomRes, 'modules'),
        pandemicModules: ok(pandemicRes, 'modules'),
        foundationCourseModules: ok(foundationRes, 'modules'),
        reflections: ok(reflectionsRes, 'reflections'),
        electives: ok(electivesRes, 'electives'),
        eceSessions: ok(eceRes, 'sessions'),
        families: ok(familiesRes, 'families'),
        familyVisits: ok(visitsRes, 'visits'),
        conferenceEntries: ok(conferenceRes, 'entries'),
        doapSessions: ok(doapRes, 'sessions'),
        professionalismAssessments: ok(professionalismRes, 'assessments'),
        internshipPostings: ok(postingsRes, 'postings'),
        internshipActivities: ok(activitiesRes, 'activities')
      });
    } catch (err) {
      console.error('Error generating UG logbook PDF:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const dashboardItems = [
    {
      id: 'elogbook',
      title: 'E-Logbook',
      icon: <FaBookMedical />,
      color: '#48C9B0',
      description: 'Log competencies & view submissions'
    },
    {
      id: 'aetcom',
      title: 'AETCOM',
      icon: <FaHandHoldingMedical />,
      color: '#9B59B6',
      description: 'Attitude, Ethics & Communication'
    },
    {
      id: 'reflections',
      title: 'Reflections',
      icon: <FaPenFancy />,
      color: '#E67E22',
      description: 'Reflective journaling'
    },
    {
      id: 'electives',
      title: 'Electives',
      icon: <FaExchangeAlt />,
      color: '#16A085',
      description: 'Elective posting log'
    },
    {
      id: 'ece',
      title: 'Early Clinical Exposure',
      icon: <FaStethoscope />,
      color: '#2980B9',
      description: 'ECE session log'
    },
    {
      id: 'fap',
      title: 'Family Adoption',
      icon: <FaHouseUser />,
      color: '#27AE60',
      description: '3-year family health tracking'
    },
    {
      id: 'pandemic',
      title: 'Pandemic Module',
      icon: <FaShieldVirus />,
      color: '#8E44AD',
      description: 'Pandemic preparedness modules'
    },
    {
      id: 'foundation-course',
      title: 'Foundation Course',
      icon: <FaUniversity />,
      color: '#D35400',
      description: 'Phase 1 orientation course'
    },
    {
      id: 'sdl',
      title: 'Self-Directed Learning',
      icon: <FaBookReader />,
      color: '#1ABC9C',
      description: 'Independent learning log'
    },
    {
      id: 'conference',
      title: 'Conference / Seminars',
      icon: <FaChalkboardTeacher />,
      color: '#3498DB',
      description: 'External conferences, workshops & seminars'
    },
    {
      id: 'doap',
      title: 'DOAP',
      icon: <FaClipboardCheck />,
      color: '#E74C3C',
      description: 'Directly observed procedural skills assessment'
    },
    {
      id: 'professionalism',
      title: 'Professionalism (FAP)',
      icon: <FaUserTie />,
      color: '#34495E',
      description: 'Formative assessment of professionalism'
    },
    {
      id: 'internship',
      title: 'Internship (CRMI)',
      icon: <FaUserMd />,
      color: '#117864',
      description: 'Rotating internship postings & procedure log'
    },
    {
      id: 'certificates',
      title: 'Certificates',
      icon: <FaCertificate />,
      color: '#c62828',
      description: 'My certificates'
    },
    {
      id: 'download-pdf',
      title: 'Download Logbook PDF',
      icon: <FaDownload />,
      color: '#5d4037',
      description: isGeneratingPDF ? 'Generating PDF...' : 'Export your complete e-logbook — all modules'
    }
  ];

  const handleDashboardItemClick = (item) => {
    if (item.comingSoon) {
      alert(`${item.title} - Coming Soon!\n\nThis feature is under development and will be available soon.`);
      return;
    }
    if (item.id === 'download-pdf') {
      if (!isGeneratingPDF) handleDownloadPDF();
      return;
    }
    if (item.id === 'elogbook') {
      navigateToView('elogbook-main');
    }
    if (item.id === 'aetcom') {
      openAetcom();
    }
    if (item.id === 'reflections') {
      openReflections();
    }
    if (item.id === 'electives') {
      navigateToView('electives-main');
    }
    if (item.id === 'ece') {
      navigateToView('ece-main');
    }
    if (item.id === 'fap') {
      navigateToView('fap-main');
    }
    if (item.id === 'pandemic') {
      navigateToView('pandemic-main');
    }
    if (item.id === 'foundation-course') {
      navigateToView('foundation-course-main');
    }
    if (item.id === 'sdl') {
      navigateToView('sdl-main');
    }
    if (item.id === 'conference') {
      navigateToView('conference-main');
    }
    if (item.id === 'doap') {
      navigateToView('doap-main');
    }
    if (item.id === 'professionalism') {
      navigateToView('professionalism-main');
    }
    if (item.id === 'internship') {
      navigateToView('internship-main');
    }
    if (item.id === 'certificates') {
      navigateToView('certificates-main');
    }
  };

  if (!user) return null;

  if (showPasswordChange) {
    return <PasswordChangeModal onPasswordChanged={handlePasswordChanged} userEmail={user.email} />;
  }

  const filteredSubjects = subjects.filter(s => {
    // Subjects with no S-domain/SH-P-level competencies have nothing a student
    // could ever log (see computeLoggable in ugSubjectRoutes.js) — showing them
    // in "Add Competency" would just be a dead end.
    if (!s.loggableCompetencies) return false;
    const term = subjectSearchTerm.trim().toLowerCase();
    if (!term) return true;
    return s.name.toLowerCase().includes(term) || s.code.toLowerCase().includes(term);
  });

  // Group subjects under every phase their loggable competencies span (a
  // subject like Medicine runs Phase 2 through Phase 3 Part 2, so it shows
  // up under each of those headings) so students always find it under
  // whichever phase — including their current one — they're actually in.
  // `phases` comes pre-sorted from the API (see ugSubjectRoutes.js);
  // subjects with none yet tagged fall into "Unphased".
  const subjectsByPhase = {};
  filteredSubjects.forEach(s => {
    const phases = (s.phases && s.phases.length) ? s.phases : ['Unphased'];
    phases.forEach(phase => {
      if (!subjectsByPhase[phase]) subjectsByPhase[phase] = [];
      subjectsByPhase[phase].push(s);
    });
  });
  const phaseGroupOrder = [...PHASE_ORDER, 'Unphased'].filter(p => subjectsByPhase[p]?.length);

  const filteredLogs = myLogs.filter(l => {
    if (submissionStatusFilter && l.status !== submissionStatusFilter) return false;
    if (submissionSubjectFilter) {
      const logSubjectId = l.subjectId?._id || l.subjectId;
      if (!logSubjectId || logSubjectId.toString() !== submissionSubjectFilter) return false;
    }
    if (submissionDateFrom && new Date(l.dateOfEncounter) < new Date(submissionDateFrom)) return false;
    if (submissionDateTo && new Date(l.dateOfEncounter) > new Date(`${submissionDateTo}T23:59:59`)) return false;
    return true;
  });
  const pageLogs = filteredLogs.slice((submissionPage - 1) * ITEMS_PER_PAGE, submissionPage * ITEMS_PER_PAGE);

  const filteredReflections = reflections.filter(r => !reflectionTab || r.type === reflectionTab);
  const pageReflections = filteredReflections.slice((reflectionPage - 1) * ITEMS_PER_PAGE, reflectionPage * ITEMS_PER_PAGE);

  // Counts the representative log per competency code (not raw log rows) so
  // an old rejected/needs_revision attempt stops counting the moment a
  // fresh attempt for that same code is logged — codeLogMap()'s precedence
  // rule already prefers the newest log.
  const needsAttentionCount = Object.values(codeLogMap())
    .filter(l => l.status === 'needs_revision' || l.status === 'rejected').length;

  // "Pending" (yellow) = due in the student's current phase. "Overdue" (red)
  // = still unlogged from a phase they've already moved past.
  const pendingSubjectIds = new Set(dueNow.due.map(d => d.subjectId));
  const overdueSubjectIds = new Set(dueNow.overdue.map(d => d.subjectId));
  const pendingCodesForSelectedSubject = new Set(
    selectedSubject ? dueNow.due.filter(d => d.subjectId === selectedSubject._id).map(d => d.competencyCode) : []
  );
  const overdueCodesForSelectedSubject = new Set(
    selectedSubject ? dueNow.overdue.filter(d => d.subjectId === selectedSubject._id).map(d => d.competencyCode) : []
  );

  return (
    <div className={`ug-dashboard-root ${(view === 'dashboard' || view === 'elogbook-main' || view === 'select-subject') ? 'ug-dashboard-root-home' : ''}`}>
      <header className="ug-header">
        <div className="ug-header-brand">
          <img src={collegeConfig.logo} alt={`${collegeConfig.shortName} Logo`} className="ug-header-logo" />
          <div>
            <div className="ug-header-title">{collegeConfig.collegeName}</div>
            <div className="ug-header-sub">UG eLogbook &middot; {user.profile?.firstName} {user.profile?.lastName}</div>
          </div>
        </div>
        <div className="ug-header-actions">
          <button className="ug-icon-btn" onClick={() => setShowNotifications(!showNotifications)}>
            <FaBell />
            {unreadCount > 0 && <span className="ug-badge">{unreadCount}</span>}
          </button>

          <div className="ug-profile-menu" ref={profileMenuRef}>
            <button
              className="ug-icon-btn ug-avatar-btn"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              title={`${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim()}
            >
              <span className="ug-avatar-circle">
                {(user.profile?.firstName?.[0] || '') + (user.profile?.lastName?.[0] || '')}
              </span>
            </button>

            {showProfileMenu && (
              <div className="ug-profile-dropdown">
                <div
                  className="ug-profile-dropdown-item"
                  onClick={() => {
                    setShowManageAccount(true);
                    setShowProfileMenu(false);
                  }}
                >
                  <FaUser /> Manage Account
                </div>
                <div className="ug-profile-dropdown-divider" />
                <div
                  className="ug-profile-dropdown-item ug-profile-dropdown-item-logout"
                  onClick={handleLogout}
                >
                  <FaSignOutAlt /> Logout
                </div>
              </div>
            )}
          </div>
        </div>

        {showNotifications && (
          <div className="ug-notif-dropdown">
            <div className="ug-notif-dropdown-head">
              <span>Notifications</span>
              <FaTimes onClick={() => setShowNotifications(false)} />
            </div>
            {notifications.length === 0 && <div className="ug-notif-empty">No notifications</div>}
            {notifications.slice(0, 10).map(n => (
              <div key={n._id} className="ug-notif-item" onClick={() => markNotificationRead(n._id)}>
                <div className="ug-notif-item-title">{n.title}</div>
                <div className="ug-notif-item-body">{n.content}</div>
              </div>
            ))}
          </div>
        )}
      </header>

      {showManageAccount && (
        <ManageAccountModal onClose={() => setShowManageAccount(false)} user={user} />
      )}

      {loading ? (
        <div className="ug-loading">Loading your eLogbook...</div>
      ) : view === 'dashboard' ? (
        <>
          <div className="ug-welcome-section">
            <h1 className="ug-welcome-title">Welcome back, {user.profile?.firstName}!</h1>
            <p className="ug-welcome-subtitle">Track your competencies and clinical training progress</p>
          </div>

          <div className="ug-dash-title">
            <h2>Dashboard</h2>
            <div className="ug-dash-title-underline"></div>
          </div>

          {dueNow.overdue.length > 0 && (
            <div
              className="ug-dash-alert ug-dash-alert-overdue"
              onClick={() => navigateToView('elogbook-main')}
            >
              <FaExclamationCircle className="ug-dash-alert-icon" />
              <div className="ug-dash-alert-text">
                <h4>{dueNow.overdue.length} {dueNow.overdue.length === 1 ? 'competency' : 'competencies'} overdue from an earlier phase</h4>
                <p>Tap to open your E-Logbook and catch up.</p>
              </div>
            </div>
          )}

          {dueNow.due.length > 0 && (
            <div
              className="ug-dash-alert"
              onClick={() => navigateToView('elogbook-main')}
            >
              <FaExclamationCircle className="ug-dash-alert-icon" />
              <div className="ug-dash-alert-text">
                <h4>{dueNow.due.length} {dueNow.due.length === 1 ? 'competency' : 'competencies'} pending{dueNow.currentPhase ? ` — ${dueNow.currentPhase}` : ''}</h4>
                <p>Tap to open your E-Logbook and start logging.</p>
              </div>
            </div>
          )}

          {needsAttentionCount > 0 && (
            <div
              className="ug-dash-alert ug-dash-alert-overdue"
              onClick={() => navigateToView('submissions')}
            >
              <FaExclamationCircle className="ug-dash-alert-icon" />
              <div className="ug-dash-alert-text">
                <h4>{needsAttentionCount} {needsAttentionCount === 1 ? 'entry' : 'entries'} need your attention</h4>
                <p>A faculty member sent these back — tap to see the feedback and log a fresh entry.</p>
              </div>
            </div>
          )}

          <div className="ug-dash-grid">
            {dashboardItems.map((item, index) => (
              <div
                key={item.id}
                className={`ug-dash-card ${item.comingSoon ? 'ug-dash-card-soon' : ''}`}
                onClick={() => handleDashboardItemClick(item)}
                style={{ animationDelay: `${index * 0.08}s` }}
              >
                {item.comingSoon && <div className="ug-dash-card-badge">Coming Soon</div>}
                <div className="ug-dash-card-icon" style={{ background: item.color }}>
                  {item.icon}
                </div>
                <h3 className="ug-dash-card-title">{item.title}</h3>
                <p className="ug-dash-card-desc">{item.description}</p>
              </div>
            ))}
          </div>

          <footer className="ug-dash-footer">
            <p>© {collegeConfig.copyrightYear} {collegeConfig.collegeName} | {collegeConfig.appName}</p>
          </footer>
        </>
      ) : view === 'elogbook-main' ? (
        <main className="ug-main">
          <button className="ug-back-btn" onClick={() => navigateToView('dashboard')}><FaChevronLeft /> Back to Dashboard</button>
          <h2 className="ug-section-title"><FaBookMedical /> E-Logbook</h2>
          <div className="ug-option-grid">
            <div className="ug-option-card" onClick={() => navigateToView('select-subject')}>
              <div className="ug-option-icon" style={{ background: '#48C9B0' }}><FaPlus /></div>
              <h3>Add Competency</h3>
              <p>Log a new clinical competency entry</p>
            </div>
            <div className="ug-option-card" onClick={() => navigateToView('submissions')}>
              <div className="ug-option-icon" style={{ background: '#3498DB' }}><FaClipboardList /></div>
              <h3>View Submission</h3>
              <p>Review the status of your submitted entries</p>
            </div>
          </div>
        </main>
      ) : view === 'select-subject' ? (
        <main className="ug-main">
          <button className="ug-back-btn" onClick={() => navigateToView('elogbook-main')}><FaChevronLeft /> Back to E-Logbook</button>
          <h2 className="ug-section-title"><FaClipboardList /> Add Competency</h2>

          <div className="ug-search-bar">
            <FaSearch className="ug-search-icon" />
            <input
              type="text"
              className="ug-search-input"
              placeholder="Search subjects by name or code..."
              value={subjectSearchTerm}
              onChange={e => setSubjectSearchTerm(e.target.value)}
            />
          </div>

          {phaseGroupOrder.map(phase => {
            const isCurrent = phase === dueNow.currentPhase;
            return (
              <div key={phase} className="ug-phase-group">
                <div className={`ug-phase-heading${isCurrent ? ' ug-phase-heading-current' : ''}`}>
                  <span className="ug-phase-heading-name">{phase === 'Unphased' ? 'Other Subjects' : phase}</span>
                  {PHASE_YEAR_LABEL[phase] && <span className="ug-phase-heading-year">{PHASE_YEAR_LABEL[phase]}</span>}
                  {isCurrent && <span className="ug-phase-heading-badge">Your Current Phase</span>}
                </div>
                <div className="ug-subject-grid">
                  {subjectsByPhase[phase].map(subject => {
                    const progress = subjectProgress(subject);
                    const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
                    return (
                      <div key={`${subject._id}-${phase}`} className="ug-subject-card" onClick={() => openSubject(subject._id)}>
                        {overdueSubjectIds.has(subject._id) ? (
                          <span className="ug-due-dot" title="Competency overdue from an earlier phase" />
                        ) : pendingSubjectIds.has(subject._id) ? (
                          <span className="ug-pending-dot" title="Competency due this phase" />
                        ) : null}
                        <div className="ug-subject-card-head">
                          <span className="ug-subject-code">{subject.code}</span>
                          <FaChevronRight />
                        </div>
                        <div className="ug-subject-name">{subject.name}</div>
                        <div className="ug-progress-bar"><div className="ug-progress-fill" style={{ width: `${pct}%` }} /></div>
                        <div className="ug-progress-text">{progress.done}/{progress.total} certified</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {filteredSubjects.length === 0 && (
            <div className="ug-due-empty">
              {subjects.length === 0
                ? 'No subjects available yet — check with admin.'
                : subjects.every(s => !s.loggableCompetencies)
                  ? 'No subjects with loggable competencies yet — check with admin.'
                  : 'No subjects match your search.'}
            </div>
          )}
        </main>
      ) : view === 'submissions' ? (
        <main className="ug-main">
          <button className="ug-back-btn" onClick={() => navigateToView('elogbook-main')}><FaChevronLeft /> Back to E-Logbook</button>
          <h2 className="ug-section-title"><FaClipboardList /> View Submissions</h2>

          <div className="ug-submission-filter">
            <label className="ug-form-label" style={{ margin: 0 }}>Filter by status</label>
            <select
              className="ug-form-input"
              value={submissionStatusFilter}
              onChange={e => setSubmissionStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="pending">Pending review</option>
              <option value="approved">Certified</option>
              <option value="needs_revision">Needs revision</option>
              <option value="rejected">Rejected</option>
            </select>

            <label className="ug-form-label" style={{ margin: 0 }}>Subject</label>
            <select
              className="ug-form-input"
              value={submissionSubjectFilter}
              onChange={e => setSubmissionSubjectFilter(e.target.value)}
            >
              <option value="">All subjects</option>
              {subjects.map(s => (
                <option key={s._id} value={s._id}>{s.name} ({s.code})</option>
              ))}
            </select>

            <label className="ug-form-label" style={{ margin: 0 }}>From</label>
            <input
              type="date"
              className="ug-form-input"
              value={submissionDateFrom}
              onChange={e => setSubmissionDateFrom(e.target.value)}
            />

            <label className="ug-form-label" style={{ margin: 0 }}>To</label>
            <input
              type="date"
              className="ug-form-input"
              value={submissionDateTo}
              onChange={e => setSubmissionDateTo(e.target.value)}
            />
          </div>

          <div className="ug-submission-list">
            {pageLogs.map(log => (
              <div key={log._id} className="ug-submission-card">
                <div className="ug-submission-top">
                  <div>
                    <span className="ug-submission-code">{log.competencyCode}</span>
                    <div className="ug-submission-text">{log.competencySnapshot?.text}</div>
                    <div className="ug-submission-subject">{subjectNameFor(log.subjectId)}</div>
                  </div>
                  <span className={`ug-status-badge ug-status-${log.status === 'approved' ? 'approved' : log.status === 'pending' ? 'pending' : 'revision'}`}>
                    {log.status === 'approved' && <FaCheckCircle />} {SUBMISSION_STATUS_LABELS[log.status] || log.status}
                  </span>
                </div>
                <div className="ug-submission-meta">
                  <span>Encounter: {new Date(log.dateOfEncounter).toLocaleDateString()}</span>
                  <span>Level: {log.levelAchieved}</span>
                  <span>Setting: {log.settingType}</span>
                </div>
                {log.review?.feedback && (
                  <div className="ug-submission-feedback">
                    <strong>Feedback:</strong> {log.review.feedback}
                  </div>
                )}
                {log.review?.reviewerName && (
                  <p className="ug-reviewer-tag">
                    {log.review.status === 'approved' ? 'Verified by' : 'Reviewed by'} {log.review.reviewerName}
                  </p>
                )}
              </div>
            ))}
            {filteredLogs.length === 0 && (
              <div className="ug-due-empty">
                {myLogs.length === 0 ? "You haven't submitted any competency entries yet." : 'No submissions match these filters.'}
              </div>
            )}
          </div>
          <Pagination currentPage={submissionPage} totalCount={filteredLogs.length} onPageChange={setSubmissionPage} />
        </main>
      ) : view === 'aetcom-main' ? (
        <main className="ug-main">
          <button className="ug-back-btn" onClick={() => navigateToView('dashboard')}><FaChevronLeft /> Back to Dashboard</button>
          <h2 className="ug-section-title"><FaHandHoldingMedical /> AETCOM</h2>
          <p className="ug-modal-hint" style={{ margin: '0 0 14px 0' }}>
            Attitude, Ethics &amp; Communication — the 27 longitudinal modules from the NMC/MCI AETCOM curriculum, one per phase of your course.
          </p>

          {aetcomLoading ? (
            <div className="ug-due-empty">Loading AETCOM modules...</div>
          ) : (
            PHASE_ORDER.map(phase => {
              const modulesInPhase = aetcomModules.filter(m => m.phase === phase);
              if (modulesInPhase.length === 0) return null;
              const completedCount = modulesInPhase.filter(m => m.status === 'completed').length;
              const phaseIsCurrent = phase === dueNow.currentPhase;
              const phaseHasDue = phaseIsCurrent && completedCount < modulesInPhase.length;
              return (
                <div key={phase} className="ug-topic-block">
                  <div className="ug-topic-name">
                    {phaseHasDue && <span className="ug-due-dot ug-due-dot-inline" title="Modules due this phase" />}
                    {phase} <span style={{ float: 'right', textTransform: 'none', fontWeight: 400 }}>{completedCount}/{modulesInPhase.length} completed</span>
                  </div>
                  {modulesInPhase.map(m => {
                    const isDue = phaseIsCurrent && m.status !== 'completed';
                    return (
                    <div key={m.number} className="ug-aetcom-module">
                      <div className="ug-competency-row">
                        <div className="ug-competency-main">
                          <span className="ug-competency-code">
                            {isDue && <span className="ug-due-dot ug-due-dot-inline" title="Due this phase" />}
                            {m.number}
                          </span>
                          <span className="ug-competency-text">{m.title}</span>
                        </div>
                        <div className="ug-competency-side">
                          {m.status === 'completed' ? (
                            <span className="ug-status-badge ug-status-approved"><FaCheckCircle /> Completed</span>
                          ) : m.status === 'in_progress' ? (
                            <span className="ug-status-badge ug-status-pending">In Progress</span>
                          ) : (
                            <span className="ug-status-badge ug-status-not-started">Not Started</span>
                          )}
                          {m.review?.status === 'approved' && (
                            <span className="ug-status-badge ug-status-approved"><FaCheckCircle /> Approved</span>
                          )}
                          {m.review?.status === 'needs_revision' && (
                            <span className="ug-status-badge ug-status-revision">Needs revision</span>
                          )}
                        </div>
                      </div>
                      <p className="ug-aetcom-module-hint">
                        {m.activitiesCompleted || m.reflectionNotes
                          ? (m.activitiesCompleted || m.reflectionNotes)
                          : 'No progress recorded yet. Click "Update progress" to add notes.'}
                      </p>
                      {m.review?.feedback && (
                        <div className="ug-aetcom-faculty-feedback">
                          <strong>{m.review.status === 'needs_revision' ? 'Needs revision — ' : ''}Faculty feedback:</strong> {m.review.feedback}
                        </div>
                      )}
                      {m.review?.reviewerName && (
                        <p className="ug-reviewer-tag">
                          {m.review.status === 'approved' ? 'Verified by' : 'Reviewed by'} {m.review.reviewerName}
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: 8 }}>
                        {m.review?.status !== 'approved' && (
                          <button className="ug-log-btn" onClick={() => openAetcomModal(m)}>Update progress</button>
                        )}
                        {m.review?.status === 'needs_revision' && (
                          <button className="ug-log-btn" onClick={() => resubmitAetcomModule(m.number)}>Resubmit for review</button>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </main>
      ) : view === 'reflections-main' ? (
        <main className="ug-main">
          <button className="ug-back-btn" onClick={() => navigateToView('dashboard')}><FaChevronLeft /> Back to Dashboard</button>
          <h2 className="ug-section-title"><FaPenFancy /> Reflections</h2>
          <p className="ug-modal-hint" style={{ margin: '0 0 14px 0' }}>
            Guided reflective practice — weekly, monthly, and case-based entries. Use the framework below to structure each entry: What happened? &rarr; So what did I learn? &rarr; Now what will I do differently?
          </p>

          <div className="ug-reflection-tabs">
            {REFLECTION_TABS.map(tab => (
              <button
                key={tab.value || 'all'}
                className={`ug-reflection-tab ${reflectionTab === tab.value ? 'ug-reflection-tab-active' : ''}`}
                onClick={() => setReflectionTab(tab.value)}
              >
                {tab.label}
                <span className="ug-reflection-tab-count">
                  {tab.value ? reflections.filter(r => r.type === tab.value).length : reflections.length}
                </span>
              </button>
            ))}
          </div>

          <button className="ug-btn-primary ug-reflection-add-btn" onClick={openNewReflectionModal}>
            <FaPlus /> Add reflection
          </button>

          {reflectionsLoading ? (
            <div className="ug-due-empty">Loading reflections...</div>
          ) : (
            <div className="ug-reflection-list">
              {pageReflections
                .map(entry => (
                  <div key={entry._id} className="ug-reflection-card">
                    <div className="ug-reflection-card-top">
                      <span className={`ug-reflection-type-badge ug-reflection-type-${entry.type}`}>
                        {REFLECTION_TYPE_LABEL[entry.type]}
                      </span>
                      <span className="ug-reflection-date">{new Date(entry.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      {entry.review?.status === 'approved' && <span className="ug-status-badge ug-status-approved"><FaCheckCircle /> Approved</span>}
                      {entry.review?.status === 'needs_revision' && <span className="ug-status-badge ug-status-revision">Needs revision</span>}
                      {(!entry.review || entry.review.status === 'pending') && <span className="ug-status-badge ug-status-pending">Pending review</span>}
                    </div>
                    <h4 className="ug-reflection-period">{entry.period}</h4>

                    <div className="ug-reflection-section-label">Reflection</div>
                    <p className="ug-reflection-text">{entry.reflectionText}</p>

                    {entry.learningOutcome && (
                      <>
                        <div className="ug-reflection-section-label">Learning Outcome / Action Plan</div>
                        <p className="ug-reflection-text">{entry.learningOutcome}</p>
                      </>
                    )}

                    {(entry.review?.feedback || entry.facultyFeedback) && (
                      <>
                        <div className="ug-reflection-section-label">Faculty Feedback</div>
                        <p className="ug-reflection-text ug-reflection-faculty-feedback">{entry.review?.feedback || entry.facultyFeedback}</p>
                      </>
                    )}
                    {entry.review?.reviewerName && (
                      <p className="ug-reviewer-tag">
                        {entry.review.status === 'approved' ? 'Verified by' : 'Reviewed by'} {entry.review.reviewerName}
                      </p>
                    )}

                    <div className="ug-reflection-card-actions">
                      {entry.review?.status !== 'approved' && (
                        <>
                          <button className="ug-reflection-icon-btn" onClick={() => openEditReflectionModal(entry)} title="Edit"><FaPencilAlt /></button>
                          <button className="ug-reflection-icon-btn ug-reflection-icon-btn-danger" onClick={() => deleteReflection(entry)} title="Delete"><FaTrash /></button>
                        </>
                      )}
                      {entry.review?.status === 'needs_revision' && (
                        <button className="ug-log-btn" onClick={() => resubmitReflection(entry)}>Resubmit for review</button>
                      )}
                    </div>
                  </div>
                ))}
              {filteredReflections.length === 0 && (
                <div className="ug-due-empty">
                  {reflections.length === 0
                    ? "You haven't added any reflections yet. Tap \"Add reflection\" to write your first entry."
                    : 'No reflections in this category yet.'}
                </div>
              )}
            </div>
          )}
          <Pagination currentPage={reflectionPage} totalCount={filteredReflections.length} onPageChange={setReflectionPage} />
        </main>
      ) : view === 'electives-main' ? (
        <UGElectives onBack={() => navigateToView('dashboard')} subjects={subjects} />
      ) : view === 'ece-main' ? (
        <UGEce onBack={() => navigateToView('dashboard')} />
      ) : view === 'fap-main' ? (
        <UGFap onBack={() => navigateToView('dashboard')} />
      ) : view === 'pandemic-main' ? (
        <UGPandemicModule onBack={() => navigateToView('dashboard')} />
      ) : view === 'foundation-course-main' ? (
        <UGFoundationCourse onBack={() => navigateToView('dashboard')} />
      ) : view === 'sdl-main' ? (
        <UGSdl onBack={() => navigateToView('dashboard')} />
      ) : view === 'conference-main' ? (
        <UGConference onBack={() => navigateToView('dashboard')} />
      ) : view === 'doap-main' ? (
        <UGDoap onBack={() => navigateToView('dashboard')} />
      ) : view === 'professionalism-main' ? (
        <UGProfessionalism onBack={() => navigateToView('dashboard')} />
      ) : view === 'internship-main' ? (
        <UGInternship onBack={() => navigateToView('dashboard')} />
      ) : view === 'certificates-main' ? (
        <UGCertificates onBack={() => navigateToView('dashboard')} />
      ) : (
        <main className="ug-main">
          <button className="ug-back-btn" onClick={() => navigateToView('select-subject')}><FaChevronLeft /> Back to subjects</button>
          <h2 className="ug-section-title">{selectedSubject.name} ({selectedSubject.code})</h2>
          {selectedSubject.topics.map(topic => {
            const loggableCompetencies = topic.competencies.filter(c => c.isActive && c.loggable);
            if (loggableCompetencies.length === 0) return null;
            return (
              <div key={topic.topicId} className="ug-topic-block">
                <div className="ug-topic-name">{topic.name}</div>
                {loggableCompetencies.map(c => {
                  const log = codeLogMap()[c.code];
                  const status = log?.status;
                  return (
                    <div key={c.code} className="ug-competency-row">
                      <div className="ug-competency-main">
                        <span className="ug-competency-code">{c.code}</span>
                        <span className="ug-competency-text">{c.text}</span>
                        {(status === 'needs_revision' || status === 'rejected') && log?.review?.feedback && (
                          <p className="ug-competency-feedback">
                            <strong>{status === 'rejected' ? 'Rejected' : 'Needs revision'} — faculty feedback:</strong> {log.review.feedback}
                          </p>
                        )}
                        {log?.review?.reviewerName && (
                          <p className="ug-reviewer-tag">
                            {status === 'approved' ? 'Verified by' : 'Reviewed by'} {log.review.reviewerName}
                          </p>
                        )}
                      </div>
                      <div className="ug-competency-side">
                        {overdueCodesForSelectedSubject.has(c.code) && <span className="ug-due-flash">Overdue</span>}
                        {pendingCodesForSelectedSubject.has(c.code) && <span className="ug-pending-flash">Pending</span>}
                        {status === 'approved' && <span className="ug-status-badge ug-status-approved"><FaCheckCircle /> Certified</span>}
                        {status === 'pending' && <span className="ug-status-badge ug-status-pending">Pending review</span>}
                        {status === 'needs_revision' && <span className="ug-status-badge ug-status-revision">Needs revision</span>}
                        {status === 'rejected' && <span className="ug-status-badge ug-status-revision">Rejected</span>}
                        {(!status || status === 'rejected' || status === 'needs_revision') && (
                          <button className="ug-log-btn" onClick={() => openLogModal(c)}>Log competency entry</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </main>
      )}

      {logModalCompetency && (
        <div className="ug-modal-overlay" onClick={() => !submitting && setLogModalCompetency(null)}>
          <div className="ug-modal" onClick={e => e.stopPropagation()}>
            <div className="ug-modal-head">
              <h3>Log competency entry</h3>
              <FaTimes onClick={() => !submitting && setLogModalCompetency(null)} />
            </div>
            <p className="ug-modal-competency">{logModalCompetency.code} — {logModalCompetency.text}</p>
            <p className="ug-modal-hint">Submit only after the encounter or supervised activity is ready for faculty review.</p>

            <label className="ug-form-label">Level achieved</label>
            <select
              className="ug-form-input"
              value={logForm.levelAchieved}
              onChange={e => setLogForm({ ...logForm, levelAchieved: e.target.value })}
            >
              <option value="">Select level achieved</option>
              {levelOptionsFor(logModalCompetency.level).map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>

            <label className="ug-form-label">Date</label>
            <input
              type="date"
              className="ug-form-input"
              value={logForm.dateOfEncounter}
              onChange={e => setLogForm({ ...logForm, dateOfEncounter: e.target.value })}
            />

            <label className="ug-form-label">Setting</label>
            <select
              className="ug-form-input"
              value={logForm.settingType}
              onChange={e => setLogForm({ ...logForm, settingType: e.target.value })}
            >
              <option value="">Select setting</option>
              {SETTING_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>

            <label className="ug-form-label">Brief case/setting descriptor (optional)</label>
            <input
              type="text"
              className="ug-form-input"
              placeholder='e.g. "35F, OPD, suspected UTI"'
              value={logForm.caseContext}
              onChange={e => setLogForm({ ...logForm, caseContext: e.target.value })}
            />

            <label className="ug-form-label">Note (optional)</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              value={logForm.note}
              onChange={e => setLogForm({ ...logForm, note: e.target.value })}
            />

            <div className="ug-modal-actions">
              <button className="ug-btn-secondary" onClick={() => setLogModalCompetency(null)} disabled={submitting}>Cancel</button>
              <button className="ug-btn-primary" onClick={submitLog} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit for review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {aetcomModalModule && (
        <div className="ug-modal-overlay" onClick={() => !aetcomSubmitting && setAetcomModalModule(null)}>
          <div className="ug-modal" onClick={e => e.stopPropagation()}>
            <div className="ug-modal-head">
              <h3>Update progress</h3>
              <FaTimes onClick={() => !aetcomSubmitting && setAetcomModalModule(null)} />
            </div>
            <p className="ug-modal-competency">{aetcomModalModule.number} — {aetcomModalModule.title}</p>

            <label className="ug-form-label">Status</label>
            <select
              className="ug-form-input"
              value={aetcomForm.status}
              onChange={e => setAetcomForm({ ...aetcomForm, status: e.target.value })}
            >
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>

            <label className="ug-form-label">Activities completed</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              placeholder="e.g. Attended the small-group session, participated in the role-play case discussion..."
              value={aetcomForm.activitiesCompleted}
              onChange={e => setAetcomForm({ ...aetcomForm, activitiesCompleted: e.target.value })}
            />

            <label className="ug-form-label">Reflection notes</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              placeholder="What did you learn from this module? How will it change your practice?"
              value={aetcomForm.reflectionNotes}
              onChange={e => setAetcomForm({ ...aetcomForm, reflectionNotes: e.target.value })}
            />

            <label className="ug-form-label">Faculty feedback</label>
            <div className="ug-aetcom-faculty-feedback">
              {aetcomModalModule.review?.feedback || aetcomModalModule.facultyFeedback || 'Faculty feedback is filled by faculty only.'}
            </div>
            {aetcomModalModule.review?.reviewerName && (
              <p className="ug-reviewer-tag">
                {aetcomModalModule.review.status === 'approved' ? 'Verified by' : 'Reviewed by'} {aetcomModalModule.review.reviewerName}
              </p>
            )}

            <div className="ug-modal-actions">
              <button className="ug-btn-secondary" onClick={() => setAetcomModalModule(null)} disabled={aetcomSubmitting}>Cancel</button>
              <button className="ug-btn-primary" onClick={submitAetcomProgress} disabled={aetcomSubmitting}>
                {aetcomSubmitting ? 'Saving...' : 'Save progress'}
              </button>
            </div>
          </div>
        </div>
      )}

      {reflectionModalEntry && (
        <div className="ug-modal-overlay" onClick={() => !reflectionSubmitting && setReflectionModalEntry(null)}>
          <div className="ug-modal" onClick={e => e.stopPropagation()}>
            <div className="ug-modal-head">
              <h3>{reflectionModalEntry === 'new' ? 'Add reflection' : 'Edit reflection'}</h3>
              <FaTimes onClick={() => !reflectionSubmitting && setReflectionModalEntry(null)} />
            </div>

            <label className="ug-form-label">Type</label>
            <select
              className="ug-form-input"
              value={reflectionForm.type}
              onChange={e => setReflectionForm({ ...reflectionForm, type: e.target.value })}
            >
              {REFLECTION_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>

            <label className="ug-form-label">{REFLECTION_PERIOD_FIELD[reflectionForm.type].label}</label>
            <input
              type="text"
              className="ug-form-input"
              placeholder={REFLECTION_PERIOD_FIELD[reflectionForm.type].placeholder}
              value={reflectionForm.period}
              onChange={e => setReflectionForm({ ...reflectionForm, period: e.target.value })}
            />

            <label className="ug-form-label">Date</label>
            <input
              type="date"
              className="ug-form-input"
              value={reflectionForm.date}
              onChange={e => setReflectionForm({ ...reflectionForm, date: e.target.value })}
            />

            <label className="ug-form-label">Reflection</label>
            <p className="ug-modal-hint" style={{ margin: '0 0 6px 0' }}>
              Framework hint: What happened? &rarr; So what did I learn? &rarr; Now what will I do differently?
            </p>
            <textarea
              className="ug-form-input ug-form-textarea"
              style={{ minHeight: 110 }}
              value={reflectionForm.reflectionText}
              onChange={e => setReflectionForm({ ...reflectionForm, reflectionText: e.target.value })}
            />

            <label className="ug-form-label">Learning outcome / Action plan (optional)</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              value={reflectionForm.learningOutcome}
              onChange={e => setReflectionForm({ ...reflectionForm, learningOutcome: e.target.value })}
            />

            <div className="ug-modal-actions">
              <button className="ug-btn-secondary" onClick={() => setReflectionModalEntry(null)} disabled={reflectionSubmitting}>Cancel</button>
              <button className="ug-btn-primary" onClick={submitReflection} disabled={reflectionSubmitting}>
                {reflectionSubmitting ? 'Saving...' : 'Save reflection'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default UGStudentDashboard;
